import {Button, Fab, Grid, makeStyles, MenuItem, useMediaQuery} from '@material-ui/core';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import MailOutlinedIcon from '@material-ui/icons/MailOutline';
import AccountImagesCard from 'Agent/Accounts/components/AccountImagesCard';
import 'Agent/Accounts/components/Card.sass';
import {AccountDetail} from 'Agent/Accounts/models/AccountDetail';
import {changeCorrespondenceFilter} from 'Agent/Policies/ducks/policyCorrespondence';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';
import {changeAnalyticsState} from 'Core/ducks/analytics';
import {ModalState, showModal} from 'Core/ducks/modal';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import RequireScope from 'Shared/components/RequireScope';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {Scope} from 'Shared/constants/Scope';
import {prop} from 'Shared/utilities/prop';
import {UserClaims} from 'Users/models/Claims';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import AccountDetailSection from './AccountDetail/AccountDetailSection';
import {Payments, Invoices, PendingPayment} from './AccountDetail/AccountBillingComponents';
import { themeColors } from 'Theme';

const useAccountDetailStyles = makeStyles(styleTheme => ({
  accountDetailsMain: {
    alignSelf: 'center',
    flexWrap: 'wrap',
    width: '100%',
    [styleTheme.breakpoints.up('lg')]: {
      width: 'calc(80% + 3rem)',
      display: 'flex',
      flexWrap: 'wrap',
    },
  },
  pageTitle: {
    fontWeight: 'bold',
    padding: '1rem 0',
    color: '#333333',
    fontSize: '1.25rem',
  },
  container: {
    width: '100%',
    margin: '0',
  },
  actionButton: {
    borderRadius: '0.5rem',
    marginRight: '0.75rem',
    padding: '0.5rem 1.5rem',
    backgroundColor: themeColors.brightBlue_100,
    boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
    color: themeColors.blue_600,
    fontWeight: 'bold',
    '&:last-of-type': {
      marginRight: '0',
    },
    '&:hover': {
      color: themeColors.primaryWhite,
      backgroundColor: themeColors.blue_400,
    },
    [styleTheme.breakpoints.down('sm')]: {
      marginLeft: '0rem',
      marginRight: '0.75rem',
    },
  },
  separator: {
    width: '100%',
    border: '1px solid #ccc',
  },
  header: {
    display: 'flex',
    width: '100%',
    padding: '0 1rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    [styleTheme.breakpoints.down('md')]: {
      marginTop: '1.5rem',
      marginBottom: '0.5rem',
    },
  },
  actionMenuFab: {
    marginRight: '1rem',
  },
  actionButtonsGrid: {
    margin: '0',
  },
  actionMenu: {
    width: '100%',
    height: '100%',
  },
}));

type AccountDetailsCardProps = {
  accountDetail: AccountDetail;
  userClaims: UserClaims;
} & WithSelectedAgencyProps;

const AccountDetailCards = ({accountDetail, userClaims, selectedAgency}: AccountDetailsCardProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
  const scopes = useSelector(selectUserScopes);
  const styles = useAccountDetailStyles();
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const accountSource = pipe(
    O.fromNullable(accountDetail.source),
    O.map(prop('name')),
    O.chain(O.fromNullable),
    O.getOrElse(() => '')
  );
  const handleRequestPIR = () => {
    dispatch(
      showModal(
        ModalState.RequestPolicyInfoReport({
          userClaims,
          accountDetail,
          email: selectedAgency.email,
        })
      )
    );
    dispatch(
      changeAnalyticsState({
        page: analytics.page,
        ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.AGTAC_VIEW_DETAILS_REQUEST_PIR},
      })
    );
  };

  const handleViewCorrespondences = () => {
    dispatch(
      changeCorrespondenceFilter({
        accountNumber: String(accountDetail.number),
        policyNumber: '',
        insuredName: '',
      })
    );
    history.push(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES(undefined, accountDetail.number));
  };

  const handleViewPolicies = () => {
    dispatch(
      changeAnalyticsState({
        page: analytics.page,
        ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.AGTAC_VIEW_DETAILS_VIEW_POLICIES},
      })
    );
    history.push(PolicyRouteConstants.POLICIES_BY_ACCOUNT(accountDetail?.number ?? ''));
  };

  return (
    <div className={styles.accountDetailsMain}>
      <Grid container className={styles.header}>
        <Grid item>
          <h1 className={styles.pageTitle} data-dd-privacy="mask">
            Account # {accountDetail.number}
          </h1>
        </Grid>
        <Grid item>
          <Grid container spacing={4} className={`${styles.container} ${isMobile ? styles.actionButtonsGrid : ''}`}>
            {isMobile ? (
              <Fab size="small" className={styles.actionMenuFab} id="action-menu-fab">
                <ActionsMenu className={styles.actionMenu}>
                  <RequireScope scopes={scopes} scope={Scope.PolicySearch}>
                    <MenuItem
                      aria-label="view policies for this account"
                      color="primary"
                      id={`view-policies-btn-${accountDetail.id}`}
                      onClick={handleViewPolicies}
                      style={{marginRight: '0'}}
                    >
                      View Policies
                    </MenuItem>
                  </RequireScope>
                  {accountSource && accountSource === 'ORDINARY' && (
                    <RequireScope scopes={scopes} scope={Scope.PolicySearch}>
                      <MenuItem
                        aria-label="request policy information report for this account"
                        color="primary"
                        id={`request-pir-btn-${accountDetail.id}`}
                        style={{marginRight: '0'}}
                        onClick={handleRequestPIR}
                      >
                        Request PIR
                      </MenuItem>
                    </RequireScope>
                  )}
                  <RequireScope scopes={scopes} scope={Scope.PolicySearch}>
                    <MenuItem
                      aria-label="view correspondences for this account"
                      color="primary"
                      id={`view-correspondences-btn-${accountDetail.id}`}
                      onClick={handleViewCorrespondences}
                    >
                      View Correspondences
                    </MenuItem>
                  </RequireScope>
                </ActionsMenu>
              </Fab>
            ) : (
              <>
                <RequireScope scopes={scopes} scope={Scope.PolicySearch}>
                  <Button
                    aria-label="view policies for this account"
                    className={styles.actionButton}
                    color="primary"
                    id={`view-policies-btn-${accountDetail.id}`}
                    onClick={handleViewPolicies}
                    style={{marginRight: `${isMobile ? '1rem' : '0.75rem'}`}}
                    variant="contained"
                    startIcon={<DescriptionOutlinedIcon />}
                  >
                    View Policies
                  </Button>
                </RequireScope>
                {accountSource && accountSource === 'ORDINARY' && (
                  <RequireScope scopes={scopes} scope={Scope.SearchPolicyReports}>
                    <Button
                      aria-label="request policy information report for this account"
                      className={styles.actionButton}
                      color="primary"
                      id={`request-pir-btn-${accountDetail.id}`}
                      onClick={handleRequestPIR}
                      variant="contained"
                      startIcon={<AssessmentOutlinedIcon style={{transform: 'rotate(90deg)'}} />}
                    >
                      Request PIR
                    </Button>
                  </RequireScope>
                )}
                <RequireScope scopes={scopes} scope={Scope.SearchPolicyCorrespondence}>
                  <Button
                    id={`view-correspondences-btn-${accountDetail.id}`}
                    aria-label="view correspondences for this account"
                    color="primary"
                    variant="contained"
                    className={styles.actionButton}
                    onClick={handleViewCorrespondences}
                    startIcon={<MailOutlinedIcon />}
                  >
                    View Correspondences
                  </Button>
                </RequireScope>
              </>
            )}
          </Grid>
        </Grid>
        <hr className={styles.separator} />
      </Grid>
      <Grid container spacing={3} className={styles.container}>
        <Grid item xs={12} md={3} role="complementary">
          <AccountDetailSection accountDetail={accountDetail} />
        </Grid>
        <Grid item xs={12} md={9} role="main">
          {Array.isArray(accountDetail.payments) && accountDetail.payments.length > 0 && (
            <RequireScope scopes={scopes} scope={Scope.ViewAccountBilling}>
              <Payments
                accountNumber={accountDetail?.number}
                accountPayments={accountDetail.payments}
                accountSource={accountDetail.source}
                eisFlag={Boolean(accountDetail.eisFlag)}
                paymentFrequency={accountDetail?.paymentFrequency}
              />
            </RequireScope>
          )}
          {accountDetail.pendingPayment && <PendingPayment pendingPayment={accountDetail.pendingPayment} />}
          {Array.isArray(accountDetail.invoices) && accountDetail.invoices.length > 0 && (
            <Invoices
              accountInvoices={accountDetail.invoices}
              accountNumber={accountDetail.number}
              eisFlag={Boolean(accountDetail.eisFlag)}
              parameters={location.search}
            />
          )}
          <RequireScope scopes={scopes} scope={Scope.ViewPolicyImges}>
            <AccountImagesCard selectedAgency={selectedAgency} eisFlag={Boolean(accountDetail.eisFlag)} />
          </RequireScope>
        </Grid>
      </Grid>
    </div>
  );
};

export default withSelectedAgency(AccountDetailCards);
